import { getMonthName, pickT } from "@repo/shared";
import { useEffect, useMemo, useState } from "react";
import { LineChart } from "@/components/charts/line-chart";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBrandColor } from "@/lib/context/brand-color-context";
import { useUserLanguage } from "@/lib/i18n/context";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ExpectedValueSnapshot } from "@/lib/types/flow";
import { getRelativeTime } from "@/lib/utils";
import { T } from "./expected-value-widget.i18n";

type ExpectedValueWidgetProps = {
  snapshot: ExpectedValueSnapshot;
  showChart?: boolean;
  showSummaryTable?: boolean;
  showYearlyTable?: boolean;
  compareWithBank?: boolean;
  language?: string;
};

/**
 * Format a number as currency
 */
type FormatNumberFn = (
  value: number,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
) => string;

function formatCurrency(amount: number, currency: string, fmt: FormatNumberFn): string {
  return `${fmt(amount, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`;
}

/**
 * Format YYYY-MM-DD to year only for grouping
 */
function getYear(dateStr: string): string {
  return dateStr.substring(0, 4);
}

/**
 * Format YYYY-MM-DD to month name
 */
function formatMonth(dateStr: string, language: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return getMonthName(date.getMonth(), language);
}

export function ExpectedValueWidget({
  snapshot,
  showChart = true,
  showSummaryTable = false,
  showYearlyTable = false,
  compareWithBank = false,
  language,
}: ExpectedValueWidgetProps) {
  const primaryColor = useBrandColor();
  const { userLanguage } = useUserLanguage();
  const { formatNumber } = useRegionalFormat();
  const lang = language || userLanguage;
  const t = pickT(T, lang);
  const displaySummaryTable = showSummaryTable;
  const displayYearlyTable = showYearlyTable;

  // Check if any withdrawals exist in the forecast
  const hasWithdrawals = useMemo(() => {
    return snapshot.forecast.some((entry) => entry.withdrawal && parseFloat(entry.withdrawal) > 0);
  }, [snapshot.forecast]);

  // Group forecast entries by year
  const yearlyData = useMemo(() => {
    const years = new Map<string, typeof snapshot.forecast>();

    for (const entry of snapshot.forecast) {
      const year = getYear(entry.date);
      if (!years.has(year)) {
        years.set(year, []);
      }
      years.get(year)!.push(entry);
    }

    return Array.from(years.entries()).map(([year, entries]) => ({
      year,
      entries,
      // Calculate yearly totals for summary
      totalDeposits: entries.reduce((sum, e) => sum + parseFloat(e.deposit), 0),
      totalReturns: entries.reduce((sum, e) => sum + parseFloat(e.returnAmount), 0),
      totalWithdrawals: entries.reduce((sum, e) => sum + parseFloat(e.withdrawal || "0"), 0),
      endValue: parseFloat(entries[entries.length - 1].value),
    }));
  }, [snapshot.forecast]);

  // Build tab list: Summary first (if enabled), then years (if enabled)
  const tabs = useMemo(() => {
    const result: Array<{ id: string; label: string; type: "summary" | "year" }> = [];
    if (displaySummaryTable) {
      result.push({ id: "summary", label: t.summary, type: "summary" });
    }
    if (displayYearlyTable) {
      for (const { year } of yearlyData) {
        result.push({ id: year, label: year, type: "year" });
      }
    }
    return result;
  }, [displaySummaryTable, displayYearlyTable, yearlyData, t.summary]);

  // Default to first tab
  const [selectedTab, setSelectedTab] = useState<string>(() => {
    return tabs.length > 0 ? tabs[0].id : "";
  });

  // Update selected tab when tabs change (e.g., when display options change)
  useEffect(() => {
    if (tabs.length > 0 && (!selectedTab || !tabs.find((t) => t.id === selectedTab))) {
      setSelectedTab(tabs[0].id);
    }
  }, [tabs, selectedTab]);

  // Bank comparison is display-gated by component config AND requires the
  // snapshot to carry the series (older snapshots don't until regenerated).
  const showBank = compareWithBank && !!snapshot.bank && snapshot.bank.length > 0;

  // Transform data for line chart
  const chartData = useMemo(() => {
    return snapshot.forecast.map((entry, index) => ({
      date: entry.date,
      value: parseFloat(entry.value),
      ...(showBank ? { bank: parseFloat(snapshot.bank![index]?.value || entry.value) } : {}),
    }));
  }, [snapshot.forecast, snapshot.bank, showBank]);

  if (!snapshot.forecast || snapshot.forecast.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noDataAvailable}</p>
      </Card>
    );
  }

  // At least one display option must be shown
  const hasAnyTable = displaySummaryTable || displayYearlyTable;
  const displayChart = showChart || !hasAnyTable;

  return (
    <div className="w-full space-y-4">
      {displayChart && (
        <div className="w-full">
          <LineChart
            data={chartData}
            height={280}
            width="container"
            primaryColor={primaryColor ?? undefined}
            lang={lang}
            extraYGridlines={1}
            bankReturnPct={showBank ? snapshot.bankReturnPct : undefined}
          />
        </div>
      )}

      {/* Summary Table Only - no tabs needed */}
      {displaySummaryTable && !displayYearlyTable && (
        <div className="space-y-2">
          <h5 className="text-sm font-medium">{t.summary}</h5>
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.year}</TableHead>
                <TableHead className="text-right">{t.deposits}</TableHead>
                {hasWithdrawals && <TableHead className="text-right">{t.withdrawals}</TableHead>}
                <TableHead className="text-right">{t.returns}</TableHead>
                <TableHead className="text-right">{t.value}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {yearlyData.map(
                ({ year, totalDeposits, totalWithdrawals, totalReturns, endValue }) => (
                  <TableRow key={year}>
                    <TableCell className="font-medium">{year}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(totalDeposits, snapshot.currency, formatNumber)}
                    </TableCell>
                    {hasWithdrawals && (
                      <TableCell className="text-right">
                        {formatCurrency(totalWithdrawals, snapshot.currency, formatNumber)}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      {formatCurrency(totalReturns, snapshot.currency, formatNumber)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(endValue, snapshot.currency, formatNumber)}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Both tables enabled - use tabs for switching between summary and yearly */}
      {displaySummaryTable && displayYearlyTable && (
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="justify-start gap-1">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className="text-xs px-2 py-1">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Summary Table */}
          {displaySummaryTable && (
            <TabsContent value="summary">
              <Table variant="data">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.year}</TableHead>
                    <TableHead className="text-right">{t.deposits}</TableHead>
                    {hasWithdrawals && (
                      <TableHead className="text-right">{t.withdrawals}</TableHead>
                    )}
                    <TableHead className="text-right">{t.returns}</TableHead>
                    <TableHead className="text-right">{t.value}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {yearlyData.map(
                    ({ year, totalDeposits, totalWithdrawals, totalReturns, endValue }) => (
                      <TableRow key={year}>
                        <TableCell className="font-medium">{year}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(totalDeposits, snapshot.currency, formatNumber)}
                        </TableCell>
                        {hasWithdrawals && (
                          <TableCell className="text-right">
                            {formatCurrency(totalWithdrawals, snapshot.currency, formatNumber)}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          {formatCurrency(totalReturns, snapshot.currency, formatNumber)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(endValue, snapshot.currency, formatNumber)}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          )}

          {/* Yearly Tables (monthly breakdown) */}
          {displayYearlyTable &&
            yearlyData.map(({ year, entries }) => (
              <TabsContent key={year} value={year}>
                <Table variant="data">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.month}</TableHead>
                      <TableHead className="text-right">{t.deposit}</TableHead>
                      {hasWithdrawals && (
                        <TableHead className="text-right">{t.withdrawal}</TableHead>
                      )}
                      <TableHead className="text-right">{t.return}</TableHead>
                      <TableHead className="text-right">{t.value}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.date}>
                        <TableCell className="font-medium">
                          {formatMonth(entry.date, lang)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(
                            parseFloat(entry.deposit),
                            snapshot.currency,
                            formatNumber,
                          )}
                        </TableCell>
                        {hasWithdrawals && (
                          <TableCell className="text-right">
                            {formatCurrency(
                              parseFloat(entry.withdrawal || "0"),
                              snapshot.currency,
                              formatNumber,
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          {formatCurrency(
                            parseFloat(entry.returnAmount),
                            snapshot.currency,
                            formatNumber,
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(parseFloat(entry.value), snapshot.currency, formatNumber)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            ))}
        </Tabs>
      )}

      {/* Yearly Table Only - no tabs needed */}
      {displayYearlyTable && !displaySummaryTable && (
        <Tabs value={selectedTab} onValueChange={setSelectedTab}>
          <TabsList className="justify-start gap-1">
            {yearlyData.map(({ year }) => (
              <TabsTrigger key={year} value={year} className="text-xs px-2 py-1">
                {year}
              </TabsTrigger>
            ))}
          </TabsList>

          {yearlyData.map(({ year, entries }) => (
            <TabsContent key={year} value={year}>
              <Table variant="data">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.month}</TableHead>
                    <TableHead className="text-right">{t.deposit}</TableHead>
                    {hasWithdrawals && <TableHead className="text-right">{t.withdrawal}</TableHead>}
                    <TableHead className="text-right">{t.return}</TableHead>
                    <TableHead className="text-right">{t.value}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.date}>
                      <TableCell className="font-medium">{formatMonth(entry.date, lang)}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(parseFloat(entry.deposit), snapshot.currency, formatNumber)}
                      </TableCell>
                      {hasWithdrawals && (
                        <TableCell className="text-right">
                          {formatCurrency(
                            parseFloat(entry.withdrawal || "0"),
                            snapshot.currency,
                            formatNumber,
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        {formatCurrency(
                          parseFloat(entry.returnAmount),
                          snapshot.currency,
                          formatNumber,
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(parseFloat(entry.value), snapshot.currency, formatNumber)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {snapshot.timestamp && (
        <div className="text-xs text-muted-foreground">
          {t.calculated(getRelativeTime(snapshot.timestamp, lang))}
        </div>
      )}
    </div>
  );
}
